
import {
  computed, defineComponent, onBeforeMount, reactive, watch,
} from 'vue';
import { Modal, Table } from 'ant-design-vue';
import { useTelaBase } from '@/core/composables/TelaBase';
import TituloPadrao from '@/core/components/Tela/TituloPadrao.vue';
import Icone from '@/core/components/Icone.vue';
import MensagemSemDados from '@/core/components/Tela/MensagemSemDados.vue';
import ColunaGrade from '@/core/components/Tela/ColunaGrade.vue';
import { useGradeBase } from '@/core/composables/GradeBase';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import storeSistema from '@/store/storeSistema';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import ServicoPCP from '@/servicos/PCP/ServicoPCP';
import { ISimulacaoProducao } from '@/models/Entidades/PCP/ISimulacaoProducao';
import SimulacaoProducaoModal from './SimulacaoProducaoModal.vue';
import { EStatusRetornoRequisicao } from '@/core/models/IRetornoRequisicao';

export default defineComponent({
  name: 'SimulacaoProducaoIndex',
  components: {
    Icone,
    ColunaGrade,
    MensagemSemDados,
    TituloPadrao,
    SimulacaoProducaoModal,
    ATable: Table,
  },
  setup() {
    const servicoPCP = new ServicoPCP();
    const {
      telaBase, apresentarMensagemSucesso,
    } = useTelaBase();
    const { gradeBase } = useGradeBase();

    const state = reactive({
      empresaSelecionada: 0,
      exibirModal: false,
      operacaoLancamento: {} as ITelaOperacao,
      simulacoes: [] as ISimulacaoProducao[],
      atualizarGrid: false,
    });

    const colunas = [
      {
        title: 'Descrição',
        dataIndex: 'descricao',
        key: 'descricao',
        ordering: true,
        filtering: true,
        ellipsis: true,
      },
      {
        title: 'Data',
        dataIndex: 'dataSimulacao',
        key: 'dataSimulacao',
        ordering: true,
        filtering: true,
        width: 120,
        align: 'center' as any,
      },
      {
        title: 'Observação',
        dataIndex: 'observacao',
        key: 'observacao',
        ordering: true,
        filtering: true,
        ellipsis: true,
      },
      {
        title: 'Ações',
        key: 'acoes',
        width: 100,
        align: 'center' as any,
      },
    ];

    const simulacoesFiltradasOrdenadas = computed(() => {
      let dados = [...state.simulacoes];

      Object.entries(gradeBase.filtrosCabecalho).forEach(([identificador, valor]) => {
        const termo = (valor ?? '').toString().trim().toLowerCase();
        if (!termo) {
          return;
        }

        dados = dados.filter((item) => {
          if (identificador === 'dataSimulacao') {
            const dataFormatada = UtilitarioData.aplicaFormatoData(item.dataSimulacao).toLowerCase();
            return dataFormatada.includes(termo);
          }

          const valorCampo = String((item as any)[identificador] ?? '').toLowerCase();
          return valorCampo.includes(termo);
        });
      });

      gradeBase.ordenacaoSelecionada.forEach((itemOrdenacao) => {
        dados.sort((itemA, itemB) => {
          const valorA = String((itemA as any)[itemOrdenacao.identificador] ?? '').toLowerCase();
          const valorB = String((itemB as any)[itemOrdenacao.identificador] ?? '').toLowerCase();
          if (valorA === valorB) {
            return 0;
          }

          const ascendente = valorA > valorB ? 1 : -1;
          return itemOrdenacao.ordem === 'DESC' ? ascendente * -1 : ascendente;
        });
      });

      return dados;
    });

    function atualizarGradeCabecalho() {
      state.atualizarGrid = !state.atualizarGrid;
    }

    async function buscarSimulacoes() {
      telaBase.carregando = true;
      state.simulacoes = await servicoPCP.obterListaSimulacao(state.empresaSelecionada);
      telaBase.carregando = false;
    }

    onBeforeMount(async () => {
      state.empresaSelecionada = storeSistema.getters.codigoEmpresaOperacao();
      gradeBase.ordenacaoSelecionada = [];
      gradeBase.filtrosCabecalho = {};
      await buscarSimulacoes();
    });

    function incluir() {
      state.operacaoLancamento = {
        tipoPermissaoDados: EPermissaoDados.Incluir,
        codigoRegistro: 0,
        empresaSelecionada: state.empresaSelecionada,
      } as ITelaOperacao;
      state.exibirModal = true;
    }

    function editar(codigoRegistro: number) {
      state.operacaoLancamento = {
        tipoPermissaoDados: EPermissaoDados.Alterar,
        codigoRegistro,
        empresaSelecionada: state.empresaSelecionada,
      } as ITelaOperacao;
      state.exibirModal = true;
    }

    async function excluir(codigo: number) {
      const retorno = await servicoPCP.excluirSimulacao(codigo);
      if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
        apresentarMensagemSucesso(retorno.mensagem);
        await buscarSimulacoes();
      } else {
        Modal.warning({
          title: 'Não foi possível concluir a exclusão!',
          content: retorno.mensagem,
        });
      }
    }

    function confirmarExclusao(simulacao: ISimulacaoProducao) {
      Modal.confirm({
        title: 'Você confirma a exclusão da simulação?',
        content: `"${simulacao.descricao}"`,
        okText: 'Sim',
        okType: 'danger',
        cancelText: 'Não',
        centered: true,
        onOk: async () => { await excluir(simulacao.codigo); },
      });
    }

    function atualizarInformacoesTela() {
      state.atualizarGrid = true;
    }

    watch(() => state.exibirModal, (newValue) => {
      if (!newValue && state.atualizarGrid) {
        buscarSimulacoes();
        state.atualizarGrid = false;
      }
    });

    return {
      state,
      telaBase,
      gradeBase,
      colunas,
      simulacoesFiltradasOrdenadas,
      incluir,
      editar,
      confirmarExclusao,
      atualizarInformacoesTela,
      atualizarGradeCabecalho,
      UtilitarioData,
    };
  },
});
